/**
 * Local-only impersonation tool. Defense-in-depth: this component only mounts on
 * localhost (see app.tsx) and the backend only honours the devUserId cookie / dev
 * endpoints in test mode, so the whole surface is inert when deployed for real.
 */
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faUserSecret } from '@fortawesome/pro-light-svg-icons';
import { trpc } from '@/utils/trpc-client';
import { useAuth } from '@/auth/use-auth';
import { UserSwitcher } from '@/features/dev/user-switcher';

const DEV_USER_COOKIE = 'devUserId';

function setDevUserCookie(userId: string): void {
  document.cookie = `${DEV_USER_COOKIE}=${encodeURIComponent(userId)}; path=/; max-age=${30 * 24 * 60 * 60}`;
}

function clearDevUserCookie(): void {
  document.cookie = `${DEV_USER_COOKIE}=; path=/; max-age=0`;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function DevPanel() {
  const [open, setOpen] = React.useState(false);
  const { identity } = useAuth();

  React.useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'x') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      setOpen(prev => !prev);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const usersQuery = trpc.devListUsers.useQuery(undefined, {
    enabled: open,
    refetchOnWindowFocus: false
  });
  const users = React.useMemo(() => usersQuery.data ?? [], [usersQuery.data]);

  function impersonate(userId: string) {
    setDevUserCookie(userId);
    // Full reload so auth/identity, the tRPC context, and all caches rebuild
    // cleanly as the new user (no stale data from the previous identity).
    window.location.reload();
  }

  function returnToDefault() {
    clearDevUserCookie();
    window.location.reload();
  }

  if (!open) return null;

  return (
    <div className="fixed bottom-4 left-4 z-[10001] w-80 rounded-lg border border-slate-700 bg-slate-900 p-4 text-slate-100 shadow-2xl">
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <FontAwesomeIcon icon={faUserSecret} className="h-4 w-4 text-amber-400" />
          Dev Panel
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-white/10"
          aria-label="Close Dev Panel">
          <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mb-3 text-xs text-slate-400">
        Impersonating{' '}
        <span className="font-medium text-slate-100">{identity?.name ?? 'Loading…'}</span>
      </div>

      <div className="mb-3">
        <label className="mb-1 block text-xs text-slate-400">Switch user</label>
        <UserSwitcher users={users} isLoading={usersQuery.isLoading} onSelect={impersonate} />
      </div>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={returnToDefault}
          className="w-full rounded-md border border-slate-600 px-3 py-1.5 text-sm text-slate-200 hover:bg-white/5">
          Return to default user
        </button>
      </div>

      <p className="mt-3 text-[11px] leading-tight text-slate-500">
        Press <kbd className="rounded bg-slate-700 px-1">x</kbd> to toggle this panel. Local-only.
      </p>
    </div>
  );
}
